import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AlertNotification from "../../components/notification";
import AddProjectModal from "../../components/project-management/AddProjectModal";
import ProjectTable from "../../components/project-management/ProjectTable";
import DeleteModal from "../../components/models/deleteModal.jsx";
import api from "../../services/apiService";
import { ROLES } from "../../constants";
import { getUserProfile } from "../../controllers/utilityController";
import { mapApiProjectToRecord, mapProjectRecord } from "./projectMockData";

const fallbackProjectTypeToIdMap = {
  Internal: 1,
  "Client Delivery": 2,
  Automation: 3,
  Compliance: 4,
  Marketing: 5,
  "Data Engineering": 6,
  Content: 7,
  Analytics: 8,
  Growth: 9,
  AI: 10,
};

const fallbackProjectTypes = Object.keys(fallbackProjectTypeToIdMap);

function ClosedProjects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [modalInstanceKey, setModalInstanceKey] = useState(0);
  const [notification, setNotification] = useState(null);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [fetchedUsers, setFetchedUsers] = useState([]);
  const [projectTypeOptions, setProjectTypeOptions] = useState(fallbackProjectTypes);
  const [projectTypeIdByName, setProjectTypeIdByName] = useState(fallbackProjectTypeToIdMap);
  const [role, setRole] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [isDeletingProject, setIsDeletingProject] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await getUserProfile();
        setRole(res?.role || "");
      } catch (error) {
        console.error("Failed to fetch user profile", error);
      }
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get("/fetch-users");
        const list = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
          ? response.data
          : [];

        setFetchedUsers(list);
        const emails = [...new Set(list.map((user) => user?.email).filter(Boolean))];
        if (emails.length > 0) {
          setAssigneeOptions(emails);
        }
      } catch (error) {
        console.error("Failed to fetch users for assignee list", error);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await api.get("/projects", {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        });

        const list = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
          ? response.data
          : [];

        setProjects(list.map((project) => mapProjectRecord(mapApiProjectToRecord(project))));
      } catch (error) {
        console.error("Failed to fetch projects", error);
      } finally {
        setIsLoadingProjects(false);
      }
    };

    fetchProjects();
  }, []);

  useEffect(() => {
    const fetchProjectTypes = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await api.get("/project-types", {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        });

        const list = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
          ? response.data
          : [];

        const normalized = list
          .map((item) => {
            const id = Number(item?.id ?? item?.projectTypeId);
            const label =
              item?.name ||
              item?.projectType ||
              item?.projectTypeName ||
              item?.type ||
              item?.title ||
              "";

            return {
              id,
              label: String(label).trim(),
            };
          })
          .filter((item) => Number.isFinite(item.id) && item.label);

        if (normalized.length > 0) {
          const idMap = normalized.reduce((acc, item) => {
            acc[item.label] = item.id;
            return acc;
          }, {});

          setProjectTypeOptions(normalized.map((item) => item.label));
          setProjectTypeIdByName(idMap);
        }
      } catch (error) {
        console.error("Failed to fetch project types", error);
      }
    };

    fetchProjectTypes();
  }, []);

  const closeProjectModal = () => {
    setShowEditProjectModal(false);
    setEditingProject(null);
  };

  const handleUpdateProject = async (formValues) => {
    if (!editingProject) return;

    const totalTasks = Number(formValues.targets) || 0;
    const existingCompleted = editingProject.completedTasks || 0;
    const completedTasks =
      formValues.status === "Completed"
        ? totalTasks
        : Math.min(existingCompleted, totalTasks);

    const assigneeIds = fetchedUsers
      .filter(
        (user) =>
          formValues.assignedUsers.includes(user?.email) &&
          Number.isFinite(Number(user?.id))
      )
      .map((user) => Number(user.id));

    const payload = {
      projectName: formValues.projectName,
      projectTypeId:
        projectTypeIdByName[formValues.projectType] ||
        fallbackProjectTypeToIdMap[formValues.projectType] ||
        1,
      status: formValues.status,
      priority: formValues.priority,
      targets: Number(formValues.targets) || 0,
      startDate: formValues.startDate,
      dueDate: formValues.dueDate,
      notes: formValues.notes || "",
      assigneeIds,
    };

    const editingBackendId = Number(editingProject.backendId);
    if (!Number.isFinite(editingBackendId)) {
      setNotification({ text: "Missing project id for update.", status: false });
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await api.put(`/projects/${editingBackendId}`, payload, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });
    } catch (error) {
      console.error("Failed to update project", error);
      setNotification({ text: "Failed to update project.", status: false });
      return;
    }

    const updatedRecord = {
      id: editingProject.projectId,
      backendId: editingBackendId,
      name: formValues.projectName,
      type: formValues.projectType,
      status: formValues.status,
      priority: formValues.priority,
      completedTasks,
      totalTasks,
      targets: payload.targets,
      startDate: formValues.startDate,
      dueDate: formValues.dueDate,
      assignedUsers: formValues.assignedUsers,
      notes: formValues.notes,
    };

    setProjects((prev) =>
      prev.map((project) =>
        project.projectId === editingProject.projectId
          ? mapProjectRecord(updatedRecord)
          : project
      )
    );

    closeProjectModal();
    setNotification({ text: "Project updated successfully!", status: true });
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setModalInstanceKey((prev) => prev + 1);
    setShowEditProjectModal(true);
  };

  const handleOpenProjectBoard = (project) => {
    const routeProjectId = project.backendId || project.projectId;
    navigate(`/admin/project-management/${routeProjectId}`, {
      state: { project },
    });
  };

  const handleDeleteProject = (project) => {
    if (role !== ROLES.ADMIN) return;
    setProjectToDelete(project);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    if (isDeletingProject) return;
    setShowDeleteModal(false);
    setProjectToDelete(null);
  };

  const handleConfirmDeleteProject = async () => {
    if (!projectToDelete) return;

    const backendId = Number(projectToDelete?.backendId);
    if (!Number.isFinite(backendId)) {
      setNotification({ text: "Missing project id for delete.", status: false });
      handleCloseDeleteModal();
      return;
    }

    setIsDeletingProject(true);
    try {
      const token = localStorage.getItem("token");
      await api.delete(`/projects/${backendId}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });

      setProjects((prev) => prev.filter((item) => item.projectId !== projectToDelete.projectId));
      setNotification({ text: "Project deleted successfully!", status: true });
      setShowDeleteModal(false);
      setProjectToDelete(null);
    } catch (error) {
      console.error("Failed to delete project", error);
      setNotification({ text: "Failed to delete project.", status: false });
    } finally {
      setIsDeletingProject(false);
    }
  };

  const closedProjects = useMemo(
    () =>
      projects.filter(
        (project) => String(project?.status || "").trim().toLowerCase() === "closed"
      ),
    [projects]
  );

  return (
    <div className="w-full max-w-full">
      <ProjectTable
        projects={closedProjects}
        loading={isLoadingProjects}
        onEditProject={handleEditProject}
        onOpenProjectBoard={handleOpenProjectBoard}
        onDeleteProject={handleDeleteProject}
        canDelete={role === ROLES.ADMIN}
      />

      <AddProjectModal
        key={modalInstanceKey}
        show={showEditProjectModal}
        onClose={closeProjectModal}
        onCreateProject={handleUpdateProject}
        users={assigneeOptions}
        projectTypes={projectTypeOptions}
        mode="edit"
        initialProject={editingProject}
      />

      <DeleteModal
        show={showDeleteModal}
        handleClose={handleCloseDeleteModal}
        handleConfirm={handleConfirmDeleteProject}
        message="Are you sure you want to delete this project? If you delete this project, all associated recruits will also be deleted, and the data will not be recoverable."
        confirmText="Delete"
        cancelText="Cancel"
        loading={isDeletingProject}
      />

      {notification && (
        <AlertNotification
          text={notification.text}
          status={notification.status}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
}

export default ClosedProjects;
